/**
 * La page d'accueil des parents.
 *
 * ## Elle est faite pour un téléphone
 *
 * 49 % des familles s'inscrivent depuis leur téléphone. La partie administrative
 * du site est faite pour un écran d'ordinateur ; celle-ci ne l'est pas, et ce
 * n'est pas une adaptation — c'est le point de départ. Une seule colonne, des
 * cibles qu'on atteint au pouce, rien qui dépasse en largeur, et ce qui compte
 * dans le premier écran.
 *
 * ## Elle répond à une question, dans cet ordre
 *
 * 1. *Ai-je quelque chose à faire ?* — le bloc « à faire », qui disparaît quand
 *    la réponse est non ;
 * 2. *Puis-je inscrire ?* — un seul bouton, qui n'apparaît que s'il mène
 *    quelque part ;
 * 3. *Où en sont mes enfants ?* — la liste, avec l'état de chaque fiche ;
 * 4. *Quelles semaines sont réservées ?* — groupées par enfant.
 *
 * L'ancienne version posait trois cartes de même poids, dont une « Situation
 * financière » désactivée depuis des mois qui n'affichait qu'un encadré bleu
 * « Fonctionnalité en cours de redéveloppement », puis deux sections de plus.
 * Rien ne disait par où commencer, et ce qui bloquait réellement l'inscription
 * n'apparaissait nulle part : le parent l'apprenait au panier, après avoir
 * choisi ses stages.
 *
 * ## Ce qui en a disparu
 *
 * Les factures. Leur statut venait de `invoices.status`, que la reprise des
 * paiements a laissé derrière elle : sur 3 715 factures, 2 256 réglées
 * s'affichaient « En attente » et 28 impayées « Payé ». Une information fausse
 * aux deux tiers ne se corrige pas par un encadré d'avertissement ; elle se
 * retire. Les factures gardent leur page, où le numéro, la date, le montant et
 * le PDF restent exacts.
 *
 * Le bouton « Test de connexion DB », aussi, qui écrivait une ligne dans
 * `click_test` à chaque clic et qu'aucune famille n'avait de raison de voir.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ChevronRight, FileText, Loader2, PlusCircle, Sparkles, UserPlus } from 'lucide-react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { useAuthStore } from '../stores/authStore';
import { useKidStore } from '../stores/kidStore';
import { supabase } from '../lib/supabase';
import { avecSessionsLisibles } from '../lib/inscriptions/sessionLisible';
import { completudeProfil, etatDuCompte } from '../lib/completude';
import BlocAFaire from '../components/accueil/BlocAFaire';
import CarteEnfant from '../components/accueil/CarteEnfant';
import Inscriptions, { type InscriptionAffichee } from '../components/accueil/Inscriptions';
import InvitationNewsletter from '../components/accueil/InvitationNewsletter';
import JourneesPedagogiquesOuvertes from '../components/accueil/JourneesPedagogiquesOuvertes';
import { fautProposerLaNewsletter } from '../lib/newsletter/invitation';
import { useNewsletterStore } from '../stores/newsletterStore';

/*
  `src/lib/database.types.ts` est perime : ni `registrations` ni la table des
  attestations n'y figurent, et ecrire `.eq('User_id_Supa', ...)` ne compile
  donc pas. Le client non type, comme dans `lib/seo/gateway.supabase.ts` : un
  type absent vaut mieux qu'un type qui ment, et la forme des lignes est
  verifiee a l'execution par `avecSessionsLisibles`. Le jour ou les types
  seront regeneres, cette indirection disparaitra d'elle-meme.
*/
const sansTypes = () => supabase as unknown as SupabaseClient;

/** Une section de la page : un titre, et ce qu'il y a dessous. */
const Section = ({
  titre,
  action,
  children,
}: {
  titre: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <section className="mt-8">
    <div className="mb-3 flex items-center justify-between gap-3">
      <h2 className="text-lg font-semibold text-gray-900">{titre}</h2>
      {action}
    </div>
    {children}
  </section>
);

const DashboardPage = () => {
  const { user, profile, fetchProfile, updateProfile } = useAuthStore();
  const { subscribe } = useNewsletterStore();
  const { kids, fetchKids, isLoading: chargementEnfants } = useKidStore();

  const [inscriptions, setInscriptions] = useState<InscriptionAffichee[]>([]);
  const [chargementInscriptions, setChargementInscriptions] = useState(true);
  const [erreurInscriptions, setErreurInscriptions] = useState<string | null>(null);
  const [nombreAttestations, setNombreAttestations] = useState(0);
  /* Vrai une fois la liste des enfants revenue : « aucun enfant » n'a de sens qu'après. */
  const [enfantsCharges, setEnfantsCharges] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (!profile) fetchProfile();
    fetchKids().finally(() => setEnfantsCharges(true));
  }, [user, profile, fetchProfile, fetchKids]);

  useEffect(() => {
    if (!user) return;

    let annule = false;

    const charger = async () => {
      setChargementInscriptions(true);
      setErreurInscriptions(null);
      try {
        const { data, error } = await sansTypes()
          .from('registrations')
          .select(`
            id,
            kid_id,
            cancellation_status,
            session:activity_id(
              stage:stage_id(title),
              start_date,
              end_date,
              center:center_id(name)
            )
          `)
          .order('created_at', { ascending: false });

        if (error) throw error;
        /*
          `avecSessionsLisibles` remplace une session masquee par la RLS : sans
          lui, `session.stage.title` leve et la page devient blanche — ce qui
          est arrive le 11 septembre 2026 a 184 familles.
        */
        const lignes = (data ?? []) as unknown as InscriptionAffichee[];
        if (!annule) setInscriptions(avecSessionsLisibles(lignes));
      } catch (err) {
        console.error('Inscriptions non chargées :', err);
        if (!annule) setErreurInscriptions('Vos inscriptions n’ont pas pu être chargées.');
      } finally {
        if (!annule) setChargementInscriptions(false);
      }
    };

    const compterAttestations = async () => {
      /*
        Le filtre par utilisateur est explicite, alors que la RLS le pose deja.
        Compter sans filtre marchait — et affichait le bon nombre — mais la
        requete ne disait pas ce qu'elle voulait : la premiere politique un peu
        plus large aurait annonce a une famille des attestations qui ne sont
        pas les siennes.
      */
      const { count, error } = await sansTypes()
        .from('Attestations fiscales')
        .select('id', { count: 'exact', head: true })
        .eq('User_id_Supa', user.id);
      if (!annule && !error && count !== null) setNombreAttestations(count);
    };

    charger();
    compterAttestations();

    return () => {
      annule = true;
    };
  }, [user]);

  const etat = useMemo(
    () =>
      etatDuCompte(
        profile,
        kids.map((k) => ({
          id: k.id,
          lignes: {
            kids: k,
            health: k.health,
            allergies: k.allergies,
            activities: k.activities,
            departure: k.departure,
            inclusion: k.inclusion,
            image_rights: k.image_rights,
          },
        })),
      ),
    [profile, kids],
  );

  /*
    La completude est calculee a partir de `kids` : chaque enfant s'y retrouve
    par son identifiant. Le `filter` qui suit ne sert donc jamais — mais il
    evite qu'un ecart futur entre les deux listes affiche l'etat d'un enfant
    sur la carte d'un autre, ce qui serait pire qu'une carte manquante.
  */
  const enfantsAffiches = useMemo(
    () =>
      kids
        .map((k) => ({ ...k, completude: etat.enfants.find((e) => e.id === k.id)?.completude }))
        .filter((k): k is typeof k & { completude: NonNullable<typeof k.completude> } =>
          Boolean(k.completude),
        ),
    [kids, etat],
  );

  /*
    L'invitation ne s'affiche qu'a un parent qui n'a rien d'autre a faire : une
    demande de plus a quelqu'un qui en a deja trois n'est pas une invitation.
  */
  const proposerLaNewsletter = fautProposerLaNewsletter(profile, etat.nombreAFaire === 0);

  const accepterLaNewsletter = async () => {
    if (!user?.email) return;
    /*
      Les deux ecritures : l'abonnement lui-meme, puis la case du profil. La
      seconde est ce que lit cette page — sans elle, la carte reviendrait au
      prochain chargement alors que l'adresse est bien inscrite.
    */
    await subscribe(user.email);
    await updateProfile({ newsletter: true, newsletter_proposee_le: new Date().toISOString() });
  };

  const refuserLaNewsletter = async () => {
    await updateProfile({ newsletter_proposee_le: new Date().toISOString() });
  };

  const hasValidNN =
    !!profile?.nnational && !['EMPTY', '000', 'NULL'].includes(profile.nnational);
  const attestationsPossibles =
    profile?.account_type === 'parent' && profile?.is_legal_guardian === true && hasValidNN;

  /*
    Le profil arrive apres l'utilisateur : la version precedente renvoyait
    `null` en attendant, et le parent voyait un ecran blanc sans savoir si le
    site chargeait ou s'il etait casse. Un indicateur de chargement dit lequel
    des deux.
  */
  /*
    Un nouveau parent — pas d'enfant, coordonnées incomplètes — commence par
    « Vos coordonnées », puis enchaîne sur la fiche de son enfant et les
    stages. Un parent qui a déjà des enfants garde son tableau de bord : le
    bloc « à faire » lui dit ce qui manque, sans le détourner de ses factures.
  */
  if (profile && enfantsCharges && kids.length === 0 && !completudeProfil(profile).complet) {
    return <Navigate to="/bienvenue" replace />;
  }

  if (!profile) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center" role="status">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" aria-hidden="true" />
        <span className="sr-only">Chargement de votre espace…</span>
      </div>
    );
  }

  const prenom = profile.prenom?.trim() || profile.organisation_name?.trim() || '';

  return (
    /*
      `max-w-2xl` : la page est concue pour une colonne, et l'etirer sur un
      grand ecran ne ferait qu'eloigner le texte de son titre. Elle reste
      lisible sur un ordinateur sans cesser d'etre faite pour un telephone.
    */
    <div className="mx-auto max-w-2xl px-4 py-8 sm:py-10">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
          {prenom ? `Bonjour ${prenom}` : 'Bonjour'}
        </h1>
        <p className="mt-1 text-gray-600">
          {etat.sansEnfant
            ? 'Commençons par ajouter votre enfant.'
            : etat.nombreAFaire > 0
              ? 'Il reste quelques informations à compléter avant d’inscrire.'
              : 'Tout est à jour. Vous pouvez inscrire vos enfants à un stage.'}
        </p>
      </header>

      {chargementEnfants && kids.length === 0 ? (
        <div className="flex justify-center py-10" role="status">
          <Loader2 className="h-7 w-7 animate-spin text-primary-600" aria-hidden="true" />
          <span className="sr-only">Chargement de vos enfants…</span>
        </div>
      ) : etat.sansEnfant ? (
        /*
          Un compte sans enfant n'a rien a lire : ni liste, ni inscriptions, ni
          attestations. Une seule chose a faire, donc un seul ecran qui la
          propose — et le profil n'est demande qu'apres, quand il servira.
        */
        <section className="rounded-2xl bg-primary-50 p-6 text-center ring-1 ring-primary-100">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white">
            <UserPlus className="h-7 w-7 text-primary-600" aria-hidden="true" />
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Ajoutez votre enfant</h2>
          <p className="mx-auto mt-2 max-w-sm text-gray-600">
            Sa fiche nous dit qui est votre enfant, ce qu’il ou elle peut faire, et qui vient
            le ou la chercher. Comptez cinq minutes — vous ne la remplirez qu’une fois.
          </p>
          <Link
            to="/kids/new"
            className="btn-primary mt-5 inline-flex min-h-[48px] w-full items-center justify-center sm:w-auto"
          >
            <PlusCircle className="mr-2 h-5 w-5" aria-hidden="true" />
            Ajouter un enfant
          </Link>
        </section>
      ) : (
        <>
          <BlocAFaire profil={etat.profil} enfants={etat.enfantsAFaire} />

          <JourneesPedagogiquesOuvertes />

          {etat.peutInscrire && (
            <Link
              to="/activities"
              className="btn-primary mt-4 flex min-h-[52px] w-full items-center justify-center text-base"
            >
              <Sparkles className="mr-2 h-5 w-5" aria-hidden="true" />
              Inscrire à un stage
            </Link>
          )}

          <Section
            titre="Mes enfants"
            action={
              <Link
                to="/kids/new"
                className="inline-flex min-h-[44px] items-center text-sm font-medium text-primary-700 hover:text-primary-800"
              >
                <PlusCircle className="mr-1 h-4 w-4" aria-hidden="true" />
                Ajouter
              </Link>
            }
          >
            <div className="space-y-2">
              {enfantsAffiches.map((enfant) => (
                <CarteEnfant key={enfant.id} enfant={enfant} />
              ))}
            </div>
          </Section>

          <Section titre="Mes inscriptions">
            {chargementInscriptions ? (
              <div className="flex justify-center py-6" role="status">
                <Loader2 className="h-6 w-6 animate-spin text-primary-600" aria-hidden="true" />
                <span className="sr-only">Chargement de vos inscriptions…</span>
              </div>
            ) : erreurInscriptions ? (
              <p className="rounded-xl bg-red-50 p-3 text-sm text-red-800 ring-1 ring-red-200">
                {erreurInscriptions}{' '}
                <Link to="/registrations" className="font-medium underline">
                  Ouvrir la page des inscriptions
                </Link>
              </p>
            ) : (
              <Inscriptions
                inscriptions={inscriptions}
                enfants={kids.map((k) => ({ id: k.id, prenom: k.prenom }))}
              />
            )}
          </Section>

          {proposerLaNewsletter && (
            <InvitationNewsletter
              onAccepter={accepterLaNewsletter}
              onRefuser={refuserLaNewsletter}
            />
          )}

          {attestationsPossibles && nombreAttestations > 0 && (
            <Section titre="Attestations fiscales">
              <Link
                to="/profile/attestations"
                className="flex min-h-[56px] items-center justify-between gap-3 rounded-xl bg-white p-4 ring-1 ring-gray-200 hover:bg-gray-50"
              >
                <span className="flex items-center gap-3">
                  <FileText className="h-5 w-5 flex-shrink-0 text-primary-600" aria-hidden="true" />
                  <span className="font-medium text-gray-900">
                    {nombreAttestations === 1
                      ? '1 attestation disponible'
                      : `${nombreAttestations} attestations disponibles`}
                  </span>
                </span>
                <ChevronRight className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
              </Link>
            </Section>
          )}
        </>
      )}
    </div>
  );
};

export default DashboardPage;
